import { useEffect, useState } from 'preact/hooks';
import './app.css';
import { Book, Chapter } from './types';

import { Header } from './components/header/header';
import { Content } from './components/content/content';
import { Sidebar } from './components/sidebar/sidebar';
import { useChapters } from './hooks/use-chapters';
import { usePlacemarkers } from './hooks/use-placemarkers';
import { SettingsProvider } from './contexts/settings-context';
import { FontLoader } from './components/font-loader/font-loader';
import { ChapterIndex } from './components/sidebar/chapter-index/chapter-index';
import { SettingsPanel } from './components/sidebar/settings-panel/settings-panel';
import { CommentsPanel } from './components/sidebar/comments-panel';
import { User } from '@novelizing/types';
import { NotFound } from './components/not-found/not-found';

interface AppProps {
  book: Book;
  user: User;
  chapter: Chapter;
  chapters: Chapter[];
  chapterPermalink: string;
}

type TSidebarStates =
  | 'chapterList'
  | 'bookInfo'
  | 'settings'
  | 'comments'
  | null;

export const App = ({
  chapters: initialChapters,
  book,
  chapter: initialChapter,
  chapterPermalink,
  user,
}: AppProps) => {
  const [sidebarState, setSidebarState] = useState<TSidebarStates>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const { chapters, nextChapter, prevChapter, chapter } = useChapters({
    initialChapters,
    initialChapter,
    chapterPermalink,
    book,
  });

  usePlacemarkers({
    bookId: book.id,
    chapterId: chapter?.id || 0,
    position: 0,
  });

  const onToggleChapterList = () => {
    setSidebarState(sidebarState === 'chapterList' ? null : 'chapterList');
  };

  const onToggleSettings = () => {
    setSidebarState(sidebarState === 'settings' ? null : 'settings');
  };

  const onToggleComments = () => {
    setSidebarState(sidebarState === 'comments' ? null : 'comments');
  };

  const onToggleTTS = () => {
    setIsPlaying(!isPlaying);
  };

  useEffect(() => {
    // Add a small delay to ensure content is loaded before scrolling
    const scrollTimeout = setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'auto', // Changed from 'smooth' to 'auto' for more reliability
      });
    }, 50); // Small delay to ensure DOM has updated

    return () => clearTimeout(scrollTimeout); // Clean up timeout on unmount or before next effect run
  }, [chapterPermalink]);

  // if (!chapter) {
  //   return (
  //     <div className="not-found">
  //       <span>Chapter not found</span>
  //     </div>
  //   );
  // }

  return (
    <SettingsProvider>
      <FontLoader />
      <Header
        chapter={chapter}
        prevChapter={prevChapter}
        nextChapter={nextChapter}
        book={book}
        onToggleChapterList={onToggleChapterList}
        onToggleSettings={onToggleSettings}
        onToggleComments={onToggleComments}
        canEdit={user?.id === book?.user?.id}
        onToggleTTS={onToggleTTS}
        isPlaying={isPlaying}
        user={user}
      />
      <main>
        {chapter && (
          <Content
            chapter={chapter}
            prevChapter={prevChapter}
            nextChapter={nextChapter}
            book={book}
            isPlaying={isPlaying}
            setIsPlaying={setIsPlaying}
          />
        )}
        {!chapter && (
          <NotFound chapters={chapters} bookPermalink={book.permalink || ''} />
        )}
        {chapter && (
          <Sidebar
            visible={!!sidebarState}
            onClose={() => setSidebarState(null)}
          >
            {sidebarState === 'chapterList' && (
              <ChapterIndex chapters={chapters} chapter={chapter} book={book} />
            )}
            {sidebarState === 'settings' && <SettingsPanel />}
            {sidebarState === 'comments' && (
              <CommentsPanel
                entityId={`chapter-${chapter?.id}`}
                userId={user.id}
              />
            )}
          </Sidebar>
        )}
      </main>
    </SettingsProvider>
  );
};
